import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
const RegionalSalesDashboard: React.FC = () => {
  const regionalKPIs = [{
    region: 'North',
    revenue: '₹2,850 Cr',
    growth: '+6.8%',
    isPositive: true,
    share: '30.8%'
  }, {
    region: 'West',
    revenue: '₹3,200 Cr',
    growth: '+8.2%',
    isPositive: true,
    share: '34.6%'
  }, {
    region: 'South',
    revenue: '₹2,100 Cr',
    growth: '+4.5%',
    isPositive: true,
    share: '22.7%'
  }, {
    region: 'East',
    revenue: '₹1,100 Cr',
    growth: '+3.2%',
    isPositive: true,
    share: '11.9%'
  }];
  const monthlyRegionalData = [{
    month: 'Jan',
    North: 228,
    West: 255,
    South: 168,
    East: 88
  }, {
    month: 'Feb',
    North: 232,
    West: 262,
    South: 172,
    East: 90
  }, {
    month: 'Mar',
    North: 238,
    West: 268,
    South: 178,
    East: 92
  }, {
    month: 'Apr',
    North: 235,
    West: 265,
    South: 175,
    East: 91
  }, {
    month: 'May',
    North: 242,
    West: 272,
    South: 182,
    East: 94
  }, {
    month: 'Jun',
    North: 248,
    West: 278,
    South: 185,
    East: 96
  }, {
    month: 'Jul',
    North: 245,
    West: 275,
    South: 188,
    East: 95
  }, {
    month: 'Aug',
    North: 252,
    West: 285,
    South: 192,
    East: 98
  }, {
    month: 'Sep',
    North: 248,
    West: 280,
    South: 190,
    East: 97
  }, {
    month: 'Oct',
    North: 255,
    West: 292,
    South: 195,
    East: 100
  }, {
    month: 'Nov',
    North: 262,
    West: 298,
    South: 198,
    East: 102
  }, {
    month: 'Dec',
    North: 268,
    West: 305,
    South: 202,
    East: 105
  }];
  const stateLeaders = [{
    state: 'Maharashtra',
    region: 'West',
    revenue: 1285,
    growth: 9.2,
    customers: 142,
    margin: 19.5
  }, {
    state: 'Gujarat',
    region: 'West',
    revenue: 1095,
    growth: 8.5,
    customers: 128,
    margin: 18.8
  }, {
    state: 'Tamil Nadu',
    region: 'South',
    revenue: 985,
    growth: 5.8,
    customers: 118,
    margin: 18.2
  }, {
    state: 'Karnataka',
    region: 'South',
    revenue: 825,
    growth: 6.2,
    customers: 105,
    margin: 17.9
  }, {
    state: 'Haryana',
    region: 'North',
    revenue: 1125,
    growth: 7.5,
    customers: 132,
    margin: 19.2
  }, {
    state: 'Uttar Pradesh',
    region: 'North',
    revenue: 985,
    growth: 6.8,
    customers: 145,
    margin: 18.5
  }, {
    state: 'Delhi',
    region: 'North',
    revenue: 485,
    growth: 5.2,
    customers: 68,
    margin: 20.1
  }, {
    state: 'West Bengal',
    region: 'East',
    revenue: 525,
    growth: 4.2,
    customers: 78,
    margin: 17.2
  }, {
    state: 'Odisha',
    region: 'East',
    revenue: 325,
    growth: 3.8,
    customers: 52,
    margin: 16.8
  }, {
    state: 'Rajasthan',
    region: 'North',
    revenue: 255,
    growth: 4.5,
    customers: 48,
    margin: 17.5
  }];
  const metroCities = [{
    city: 'Mumbai',
    region: 'West',
    revenue: 685,
    growth: 10.2,
    dealers: 45,
    marketShare: 14.8,
    topCustomer: 'Maruti Suzuki',
    customerRevenue: 285
  }, {
    city: 'Bengaluru',
    region: 'South',
    revenue: 495,
    growth: 8.5,
    dealers: 38,
    marketShare: 13.2,
    topCustomer: 'Toyota',
    customerRevenue: 185
  }, {
    city: 'Delhi NCR',
    region: 'North',
    revenue: 725,
    growth: 7.8,
    dealers: 52,
    marketShare: 15.5,
    topCustomer: 'Hyundai',
    customerRevenue: 325
  }, {
    city: 'Chennai',
    region: 'South',
    revenue: 425,
    growth: 6.5,
    dealers: 32,
    marketShare: 12.8,
    topCustomer: 'Hyundai',
    customerRevenue: 195
  }];
  const regionalAnomalies = [{
    type: 'Opportunity',
    region: 'West',
    title: 'Maharashtra Surge',
    description: 'Maharashtra revenue up 9.2% - EV component demand driving growth in Pune cluster',
    impact: 'High',
    color: 'green'
  }, {
    type: 'Risk',
    region: 'East',
    title: 'East Region Slowdown',
    description: 'East region growth at 3.2% vs company average of 5.2% - dealer network gaps identified',
    impact: 'Medium',
    color: 'yellow'
  }, {
    type: 'Alert',
    region: 'South',
    title: 'Chennai Plant Capacity',
    description: 'Chennai plant at 94% capacity - potential constraint for South region growth',
    impact: 'Medium',
    color: 'orange'
  }, {
    type: 'Opportunity',
    region: 'North',
    title: 'Delhi NCR Market Share',
    description: 'Delhi NCR market share at 15.5% - highest among metros, opportunity to expand',
    impact: 'High',
    color: 'green'
  }];
  const getRegionColor = (region: string) => {
    switch (region) {
      case 'North':
        return '#3b82f6';
      case 'West':
        return '#10b981';
      case 'South':
        return '#f59e0b';
      case 'East':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };
  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'High':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Low':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  const getAnomalyIcon = (type: string) => {
    switch (type) {
      case 'Opportunity':
        return <TrendingUpIcon size={20} className="text-green-600" data-id="element-8951" />;
      case 'Risk':
        return <AlertTriangleIcon size={20} className="text-yellow-600" data-id="element-8952" />;
      case 'Alert':
        return <AlertTriangleIcon size={20} className="text-orange-600" data-id="element-8953" />;
      default:
        return null;
    }
  };
  return <div data-id="element-8954">
      <DescriptionPanel title="Regional Sales Performance" description="Comprehensive analysis of sales performance across India's four regions and key metro cities. Track regional growth dynamics, state-level performance, and identify geographical opportunities and risks." insights={['West region leading with ₹3,200 Cr revenue (34.6% of total) and 8.2% growth', 'Maharashtra state driving West region performance with 9.2% YoY growth', 'Mumbai metro achieving highest growth at 10.2% - EV components key driver', 'East region underperforming at 3.2% growth - dealer network expansion needed']} data-id="element-8955" />
      {/* Regional KPI Bar */}
      <div className="mb-8" data-id="element-8956">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-8957">
          Regional Performance Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-8958">
          {regionalKPIs.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50 border-l-4" style={{
          borderLeftColor: getRegionColor(kpi.region)
        }} data-id="element-8959">
              <div className="flex flex-col h-full" data-id="element-8960">
                <div className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3" data-id="element-8961">
                  {kpi.region}
                </div>
                <div className="space-y-2" data-id="element-8962">
                  <div data-id="element-8963">
                    <div className="text-xs text-gray-500" data-id="element-8964">Revenue</div>
                    <div className="text-3xl font-bold text-gray-900" data-id="element-8965">
                      {kpi.revenue}
                    </div>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-8966">
                    <div data-id="element-8967">
                      <div className="text-xs text-gray-500" data-id="element-8968">Growth</div>
                      <div className={`text-lg font-semibold flex items-center ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-8969">
                        {kpi.isPositive ? <TrendingUpIcon size={14} className="mr-1" data-id="element-8970" /> : <TrendingDownIcon size={14} className="mr-1" data-id="element-8971" />}
                        {kpi.growth}
                      </div>
                    </div>
                    <div data-id="element-8972">
                      <div className="text-xs text-gray-500" data-id="element-8973">Share</div>
                      <div className="text-lg font-semibold text-gray-800" data-id="element-8974">
                        {kpi.share}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>)}
        </div>
      </div>
      {/* Monthly Regional Revenue - Stacked Bar */}
      <Card className="p-6 mb-8" data-id="element-8975">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8976">
          Monthly Revenue by Region (₹ Cr)
        </h3>
        <ResponsiveContainer width="100%" height={350} data-id="element-8977">
          <BarChart data={monthlyRegionalData} data-id="element-8978">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-8979" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-8980" />
            <YAxis tick={{
            fontSize: 12
          }} data-id="element-8981" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-8982" />
            <Legend data-id="element-8983" />
            <Bar dataKey="North" stackId="a" fill="#3b82f6" name="North" data-id="element-8984" />
            <Bar dataKey="West" stackId="a" fill="#10b981" name="West" data-id="element-8985" />
            <Bar dataKey="South" stackId="a" fill="#f59e0b" name="South" data-id="element-8986" />
            <Bar dataKey="East" stackId="a" fill="#ef4444" name="East" data-id="element-8987" />
          </BarChart>
        </ResponsiveContainer>
      </Card>
      {/* State Leaders Table */}
      <Card className="p-6 mb-8" data-id="element-8988">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8989">
          Top Performing States
        </h3>
        <div className="overflow-x-auto" data-id="element-8990">
          <table className="w-full" data-id="element-8991">
            <thead data-id="element-8992">
              <tr className="border-b border-gray-200" data-id="element-8993">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8994">
                  State
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8995">
                  Region
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8996">
                  Revenue
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8997">
                  Growth %
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8998">
                  Customers
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8999">
                  Margin %
                </th>
              </tr>
            </thead>
            <tbody data-id="element-9000">
              {stateLeaders.map((state, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-9001">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-9002">
                    {state.state}
                  </td>
                  <td className="py-3 px-4" data-id="element-9003">
                    <span className="inline-flex px-2 py-1 rounded text-xs font-medium text-white" style={{
                  backgroundColor: getRegionColor(state.region)
                }} data-id="element-9004">
                      {state.region}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-gray-800" data-id="element-9005">
                    ₹{state.revenue} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-green-600" data-id="element-9006">
                    {state.growth}%
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-9007">
                    {state.customers}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-9008">
                    {state.margin}%
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
      {/* Metro City Dashboard */}
      <div className="mb-8" data-id="element-9009">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-9010">
          Metro City Performance
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9011">
          {metroCities.map((city, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white via-gray-50 to-white" data-id="element-9012">
              <div className="flex items-center justify-between mb-4" data-id="element-9013">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-9014">{city.city}</h3>
                <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium text-white" style={{
              backgroundColor: getRegionColor(city.region)
            }} data-id="element-9015">
                  {city.region}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-9016">
                <div data-id="element-9017">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-9018">Revenue</div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-9019">
                    ₹{city.revenue} Cr
                  </div>
                </div>
                <div data-id="element-9020">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-9021">Growth</div>
                  <div className="text-2xl font-bold text-green-600 flex items-center" data-id="element-9022">
                    <TrendingUpIcon size={16} className="mr-1" data-id="element-9023" />
                    {city.growth}%
                  </div>
                </div>
                <div data-id="element-9024">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-9025">
                    Active Dealers
                  </div>
                  <div className="text-xl font-semibold text-gray-800" data-id="element-9026">
                    {city.dealers}
                  </div>
                </div>
                <div data-id="element-9027">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-9028">Market Share</div>
                  <div className="text-xl font-semibold text-gray-800" data-id="element-9029">
                    {city.marketShare}%
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-9030">
                <div className="text-xs text-gray-500 mb-2" data-id="element-9031">Top Customer</div>
                <div className="flex items-center justify-between" data-id="element-9032">
                  <span className="text-sm font-medium text-gray-800" data-id="element-9033">
                    {city.topCustomer}
                  </span>
                  <span className="text-sm font-semibold text-blue-600" data-id="element-9034">
                    ₹{city.customerRevenue} Cr
                  </span>
                </div>
              </div>
            </Card>)}
        </div>
      </div>
      {/* Regional Anomalies Panel */}
      <Card className="p-6" data-id="element-9035">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-9036">
          Regional Insights & Anomalies
        </h3>
        <div className="space-y-4" data-id="element-9037">
          {regionalAnomalies.map((anomaly, index) => <div key={index} className={`border rounded-lg p-4 ${getImpactColor(anomaly.impact)}`} data-id="element-9038">
              <div className="flex items-start" data-id="element-9039">
                <div className="mr-3 mt-1" data-id="element-9040">{getAnomalyIcon(anomaly.type)}</div>
                <div className="flex-1" data-id="element-9041">
                  <div className="flex items-center justify-between mb-2" data-id="element-9042">
                    <h4 className="text-sm font-bold" data-id="element-9043">{anomaly.title}</h4>
                    <div className="flex items-center space-x-2" data-id="element-9044">
                      <span className="inline-flex px-2 py-1 rounded text-xs font-medium text-white" style={{
                    backgroundColor: getRegionColor(anomaly.region)
                  }} data-id="element-9045">
                        {anomaly.region}
                      </span>
                      <span className="text-xs font-semibold" data-id="element-9046">
                        {anomaly.impact} Impact
                      </span>
                    </div>
                  </div>
                  <p className="text-sm" data-id="element-9047">{anomaly.description}</p>
                </div>
              </div>
            </div>)}
        </div>
      </Card>
    </div>;
};
export default RegionalSalesDashboard;